import { useStore } from '@store/index';
import { buildRoutePath, removeOrgPrefix } from '@utils/routes';
import React from 'react';
import { Navigate, useParams } from 'react-router-dom';

/**
 * Route guard for an org-scoped page whose access hook reads the selected
 * organization from the store (Workforce Console, All Tasks). Renders nothing
 * while `isLoading`, sends a denied user to Cases, and renders `children`
 * otherwise.
 *
 * It also renders nothing until the store's organization matches the URL's
 * `:orgId`. The store picks up the URL's org in an effect of an ancestor
 * (`useOrgAndPrograms`), and effects run child-first, so a deep link to
 * another org (or a fresh session) would otherwise be judged against the
 * previous org, or none, and redirected before the switch lands.
 */
export function OrgPageAccessGate({
  shouldShow,
  isLoading,
  children,
}: {
  shouldShow: boolean;
  isLoading: boolean;
  children: React.ReactNode;
}) {
  const { orgId } = useParams<{ orgId?: string }>();
  const { selectedOrganizationId, selectedProgramId } = useStore();
  const urlOrgId = orgId ? removeOrgPrefix(orgId) : undefined;

  if (isLoading || (urlOrgId && urlOrgId !== selectedOrganizationId)) return null;

  if (!shouldShow) {
    const fallback = buildRoutePath(
      '/cases',
      selectedOrganizationId || '',
      selectedProgramId || '',
    );
    return <Navigate to={fallback} replace />;
  }

  return <>{children}</>;
}
